import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { defaultMainPanelConfig, getOtherTab, isTabOption } from "@/panels/modules/panels/store/panels.store.helpers";
import type {
    AsidePanelConfig,
    AsidePanelKey,
    AsidePanelsState,
    MainPanelConfig,
    MainPanelKey,
    MainPanelsState,
    PanelsStore,
    TopbarConfig,
    TopbarState,
} from "@/panels/modules/panels/store/panels.store.types";

export const defaultAsideState: AsidePanelsState = {
    leftSidebar: {},
    rightSidebar: {},
};

export const defaultTopbarState: TopbarState = {
    topbar: {},
};

export const defaultMainState: MainPanelsState = {
    leftTop: defaultMainPanelConfig,
    leftBottom: defaultMainPanelConfig,
    rightTop: defaultMainPanelConfig,
    rightBottom: defaultMainPanelConfig,
};

const defaultState = {
    ...defaultAsideState,
    ...defaultTopbarState,
    ...defaultMainState,
};

export const usePanelsStore = create<PanelsStore>()(
    devtools(
        (set, get) => {
            return {
                ...defaultState,
                setAsidePanelConfig: (panelId: AsidePanelKey, config: Partial<AsidePanelConfig>) =>
                    set(
                        () => {
                            const currentConfig = get()[panelId];
                            const newConfig = { ...currentConfig, ...config };

                            return { [panelId]: newConfig };
                        },
                        false,
                        "setAsidePanelConfig",
                    ),
                setTopbarConfig: (config: Partial<TopbarConfig>) =>
                    set(
                        () => {
                            const currentConfig = get().topbar;
                            const newConfig = { ...currentConfig, ...config };

                            return { topbar: newConfig };
                        },
                        false,
                        "setTopbarConfig",
                    ),
                setMainPanelConfig: (panelId: MainPanelKey, config: Partial<MainPanelConfig>) =>
                    set(
                        () => {
                            const currentConfig = get()[panelId];
                            const newConfig = { ...currentConfig, ...config };

                            return { [panelId]: newConfig };
                        },
                        false,
                        "setMainPanelConfig",
                    ),
                setSelectedTab: (mainPanelKey: MainPanelKey) => (tab: string | null) => {
                    if (!tab || !isTabOption(tab)) return;

                    set(
                        (state) => ({
                            [mainPanelKey]: { ...state[mainPanelKey], selectedTab: tab },
                        }),
                        false,
                        "setSelectedTab",
                    );
                },
                setIsMinimized: (mainPanelKey: MainPanelKey) => (tab: string | null) => (isMinimized: boolean) => {
                    if (!tab || !isTabOption(tab)) return;

                    set(
                        (state) => ({
                            [mainPanelKey]: {
                                ...state[mainPanelKey],
                                [tab]: { ...state[mainPanelKey][tab], isMinimized },
                            },
                        }),
                        false,
                        "setIsMinimized",
                    );
                },
                setIsClosed: (mainPanelKey: MainPanelKey) => (tab: string | null) => (isClosed: boolean) => {
                    if (!tab || !isTabOption(tab)) return;

                    const otherTab = getOtherTab(tab);
                    const tabToSelect = isClosed ? otherTab : tab;

                    set(
                        (state) => {
                            return {
                                [mainPanelKey]: {
                                    ...state[mainPanelKey],
                                    selectedTab: tabToSelect,
                                    [tab]: { ...state[mainPanelKey][tab], isClosed },
                                },
                            };
                        },
                        false,
                        "setIsClosed",
                    );
                },
            };
        },
        {
            store: "panels",
            name: "workspace/panels",
        },
    ),
);
